/**
 * DAO Admin Suite - Root Application Component
 *
 * Wraps the router with ErrorBoundary and AuthProvider for consistent
 * error handling and role-based access control.
 * Uses route-based code splitting for optimal performance.
 * Admin routes require authentication + admin role via ProtectedRoute.
 * Blog routes (/blog/*) use a separate ProtectedRoute with requiredRoles={['admin','author']}
 * so both admins and authors can access the blog dashboard and editor (BL-096).
 * The /unauthorized route is accessible to authenticated non-admin users.
 *
 * @see BL-049.2 - Admin suite sidebar navigation (layout route pattern)
 * @see BL-029.3 - Migrate dao-admin-suite to shared auth components
 * @see BL-007.4 - Integrate AdminGuard in dao-admin-suite
 * @see FAS-7.1 - Create and Deploy dao-admin-suite
 */

import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, LoginRedirect } from '@hello-world-dao/auth';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AppLayout } from '@/components/layout';
import { buildLoginFrontdoorUrl } from '@/services/identity-service-client';
import {
  runIdentityCodeBootstrap,
  resetIdentityCodeBootstrap,
  isIdentityBootstrapInFlight,
} from '@/services/identity-code-bootstrap';
import { getIdentityJwt } from '@/services/identity-jwt-store';

// Route-based code splitting - each page loads on demand
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const KYCManagement = lazy(() => import('@/pages/KYCManagement'));
const GovernanceOversight = lazy(() => import('@/pages/GovernanceOversight'));
const SystemMonitoring = lazy(() => import('@/pages/SystemMonitoring'));
const ContentModeration = lazy(() => import('@/pages/ContentModeration'));
const Unauthorized = lazy(() => import('@/pages/Unauthorized'));

// Role Management page (BL-019.2) - admin-only role assignment and audit
const RoleManagement = lazy(() => import('@/pages/RoleManagement'));

// Event Management page (BL-025.2) - admin event CRUD and RSVP viewing
const EventsPage = lazy(() => import('@/pages/EventsPage'));

// Token Management - DOM token minting and batch distribution
const TokenManagement = lazy(() => import('@/pages/TokenManagement'));

// REG-001.4: Treasury Expansion — sub-accounts, transfers, patronage
const TreasuryLayout = lazy(() => import('@/pages/treasury/TreasuryLayout'));
const Holdings = lazy(() => import('@/pages/treasury/Holdings'));
const SubAccounts = lazy(() => import('@/pages/treasury/SubAccounts'));
const InternalTransfers = lazy(() => import('@/pages/treasury/InternalTransfers'));
const Patronage = lazy(() => import('@/pages/treasury/Patronage'));

// BL-181: Stripe Payments — admin payment history from oracle-bridge
const Payments = lazy(() => import('@/pages/treasury/Payments'));
const Payouts = lazy(() => import('@/pages/treasury/Payouts'));

// REG-001.8: Entity Transfers — cross-entity intercompany transfers
const EntityTransfers = lazy(() => import('@/pages/treasury/EntityTransfers'));

// REG-001.9: Digital Assets — digital asset registry and snapshots
const DigitalAssets = lazy(() => import('@/pages/treasury/DigitalAssets'));

// Held Burns - marketplace burn dispute window management (REG-001.2)
const HeldBurns = lazy(() => import('@/pages/HeldBurns'));

// Tax Form Review - member tax-form review queue (kyc-002-3)
const TaxFormReview = lazy(() => import('@/pages/TaxFormReview'));

// Property Triage - property-registry triage/review dashboard (propreg-001-1b)
const PropertyTriage = lazy(() => import('@/pages/PropertyTriage'));

// LP Locks - DEX liquidity lock position management (REG-001.3)
const LpLocks = lazy(() => import('@/pages/LpLocks'));

// Tester Invites - UAT Tester Portal invite management (epic-uat-admin-invites-001-3)
const UatInvites = lazy(() => import('@/pages/UatInvites'));

// Vesting Schedules - DOM token vesting schedule management (REG-001.5, BL-138)
const VestingSchedules = lazy(() => import('@/pages/admin/VestingSchedules'));
const VestingDetailPage = lazy(() => import('@/pages/admin/VestingDetailPage'));

// Create Official Org (org-007-1) - HWDAO-employee admin form consuming org-004-4 endpoint
const CreateOfficialOrg = lazy(() => import('@/pages/admin/CreateOfficialOrg'));

// Add Member to Org (org-007-2) - HWDAO-employee admin form consuming org-007-2 PR-A endpoint
const AddMemberToOrg = lazy(() => import('@/pages/admin/AddMemberToOrg'));

// Marketplace Fee Routing - fee schedule and transaction history (REG-001.7)
const MarketplaceFeeSchedule = lazy(() => import('@/pages/marketplace/FeeSchedule'));
const MarketplaceTransactions = lazy(() => import('@/pages/marketplace/Transactions'));

// User Management (BL-060.1) - browse all registered users with role/verified filters
const UserManagement = lazy(() => import('@/pages/UserManagement'));

// User Detail (BL-347) - admin-safe single-user view, target of /users row click-through
const UserDetail = lazy(() => import('@/pages/UserDetail'));

// Member Management (BL-101) - membership status, SBT minting, approve/revoke
const MemberManagement = lazy(() => import('@/pages/MemberManagement'));

// Spike: Tiptap editor feasibility validation (BL-008.3.1)
// This is a temporary experimental route - not for production use
const EditorSpike = lazy(() => import('@/pages/blog/EditorSpike'));

// Production blog editor (BL-008.3.2) - code-split for LCP < 2.5s (NFR2)
// Editor chunk must remain under 200KB gzip (NFR7)
const BlogEditorPage = lazy(() => import('@/pages/blog/BlogEditorPage'));

// Blog dashboard (BL-008.3.5) - admin dashboard or contributor dashboard based on role
const BlogDashboard = lazy(() => import('@/pages/blog/BlogDashboard'));

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );
}

/**
 * Auth loading / redirect-in-progress fallback for ProtectedRoute.
 * Shown while the session check is in flight OR while the browser is
 * navigating to the external login URL.  Prevents a blank white page
 * from being visible to unauthenticated users on non-root routes
 * (BUG-DAO-076).
 */
function AuthFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      <p className="text-sm text-gray-500">Checking authentication...</p>
    </div>
  );
}

// bl-977 / bl-985: shown when the pre-mount silent /token/refresh TRANSIENTLY failed
// (timeout / 5xx / exhausted connection-retries) rather than definitively 401'd.
// An admin holding a valid refresh cookie must NOT be bounced to the sign-in shell
// on a transient blip, so we hold this neutral loader and offer a manual retry
// instead of mounting the (unauthenticated) auth tree.
//
// The hold is BOUNDED — see TRANSIENT_ESCALATION_CAP_MS. After the wall-clock
// ceiling elapses still-transient, App escalates to a mounted (unauthenticated)
// tree so a mis-classified logged-out visitor reaches the login shell instead of an
// indefinite loader. The manual Retry remains live throughout.
//
// UX COPY + STYLING ARE PLACEHOLDER — the spinner-longer-vs-login-flash tradeoff is
// Menley's lane (bl-985 AC8 all-three review flag). Do not treat this copy as final.
function ReconnectingLoader({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen flex flex-col items-center justify-center gap-4 text-gray-600"
    >
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      <p data-testid="reconnecting-message">Reconnecting…</p>
      <button
        type="button"
        onClick={onRetry}
        data-testid="reconnecting-retry"
        className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        Retry
      </button>
    </div>
  );
}

type BootstrapUiState = 'resolving' | 'settled' | 'transient';

// bl-977 regression #4 (2026-07-04): a HARD wall-clock ceiling on the transient
// "Reconnecting…" hold, measured from App mount (page load).
//
// The original bl-977 fix parked EVERY `transient` verdict on an INDEFINITE hold
// with a manual-only Retry. Under concurrent load (the very login-burst condition
// bl-977 fixed), a genuinely-logged-out visitor's `/token/refresh` can classify
// `transient` (timeout / 5xx) instead of a clean 401 — and then the visitor is
// stuck on the loader forever, never reaching the login shell. Pre-bl-977 they got
// the login shell instantly.
//
// So after this ceiling elapses with the bootstrap still parked in `transient`
// (or an in-flight retry still unresolved), we escalate to a mounted tree:
// AuthProvider mounts unauthenticated → ProtectedRoute lands the visitor on the
// login shell. This is a UI-VERDICT DOWNGRADE ONLY — it NEVER issues another
// `/token/refresh` (bl-789 reuse-safety is untouched; no extra token rotation).
//
// TRADEOFF (deliberate): an admin holding a VALID refresh cookie who hits a real
// upstream outage lasting past this cap now also gets bounced to the login shell.
// That is strictly BETTER than pre-bl-977 (which bounced them INSTANTLY on the
// first blip) and is honestly the only safe terminal state — an indefinite hold
// is worse. The admin keeps the manual Retry throughout the hold, and a fresh
// login re-establishes the session.
//
// VALUE (12s): the cap sits BELOW boundedRefresh's full worst-case retry budget
// (~9.2s–25.2s), so an admin whose reconnect is still legitimately retrying within
// that bound can also be pre-empted — this widened tradeoff must be reflected in
// the bl-985 AC8 all-three UX review, not just a narrower "genuine outage" framing.
// It is chosen to sit comfortably below the tightest unauth-gating regression
// predicate so the mounted ProtectedRoute fallback / login bounce reliably lands
// inside that assertion window.
const TRANSIENT_ESCALATION_CAP_MS = 12_000;

// bl-1247: the ceiling on the AUTH VERDICT (distinct from TRANSIENT_ESCALATION_CAP_MS
// above, which un-parks the UI). Before bl-1247 the suite never passed
// `isInitialSessionResolving` to AuthProvider, so the bl-977 hold collapsed to a
// single `setTimeout(…, 0)`: at the 12s cap the tree force-mounted on an empty JWT
// store and ProtectedRoute bounced an admin whose slow-but-succeeding `/token/refresh`
// was still in flight. Wiring the predicate (below) SPLITS mounting from deciding —
// the tree still mounts at 12s, but the unauthenticated verdict is now deferred while
// the refresh is genuinely in flight. AuthContext's hold has no ceiling of its own
// (it re-arms a 50ms poll for as long as the predicate returns true), so the bound
// lives here.
//
// VALUE (14s) — ruled by Coby 2026-08-06 (bl-1247). It is the LARGEST value that keeps
// total time-to-bounce strictly under the 15s unauth-gating regression predicate
// (TC-PROF-007 / TC-NOTIF-004), so NO ~/git/testing spec has to move. It strictly
// dominates the pre-bl-1247 behaviour (a 12.5s refresh now wins; before it lost at
// 12.003s) and covers boundedRefresh's CONSERVATIVE 9.2s bound with margin. It does
// NOT cover boundedRefresh's FULL 25.2s designed worst case — that residual honest
// tradeoff MOVES from 12s to 14s (see the TRANSIENT_ESCALATION_CAP_MS note); a refresh
// that legitimately needs longer than 14s is still pre-empted and bounced.
const SESSION_RESOLVE_CEILING_MS = 14_000;

// bl-1247: the page-load origin the SESSION_RESOLVE_CEILING_MS is measured from —
// the SAME origin as TRANSIENT_ESCALATION_CAP_MS (both armed at App mount, below).
// Null until App mounts; a null origin means the session-resolve hold is not active.
let sessionResolveOriginMs: number | null = null;

// bl-1247: the predicate wired into AuthProvider (`isInitialSessionResolving`).
// Reports that the INITIAL SESSION REFRESH is still genuinely in flight so
// AuthContext holds its unauthenticated verdict instead of bouncing an admin who
// holds a valid `hwdao_refresh` cookie whose refresh is merely slow.
//
// ⚠️ Source discipline (do NOT "simplify" this):
//   - It reads the bootstrap module's OWN in-flight flag (isIdentityBootstrapInFlight),
//     NOT `bootstrapState` / `bootstrapStateRef` / any render value. The 12s
//     escalation FORCE-SETS bootstrapState to 'settled' (that is the only thing that
//     mounts AuthProvider with an unsettled refresh in the first place), so a
//     bootstrapState-derived predicate would return false at exactly the moment the
//     hold is needed — reproducing the bug through a different door with a fix that
//     looks correct. (bl-1247 Trap 1.)
//   - It is NOT `bootstrapPromise != null` (that never clears → parks forever). The
//     flag settles by either outcome. (bl-1247 Trap 2 / rule 1.)
//   - It is a STABLE module-scope function reference so it does not tear down and
//     restart AuthContext's poll on every AuthProvider re-render. (bl-1247 Trap 3.)
// The `&& elapsed < SESSION_RESOLVE_CEILING_MS` clause is the terminal bound: for
// every reachable input this returns false in bounded time, so the hold cannot become
// an indefinite loader (which would be bl-977 regression #4 through a new door).
function isInitialSessionResolving(): boolean {
  if (sessionResolveOriginMs === null) return false;
  return (
    isIdentityBootstrapInFlight() &&
    Date.now() - sessionResolveOriginMs < SESSION_RESOLVE_CEILING_MS
  );
}

export default function App() {
  const oracleBridgeUrl = import.meta.env.VITE_ORACLE_BRIDGE_URL || undefined;
  // uid-001-suite-login-frontdoor: unauthenticated admins now redirect to the hosted
  // login front-door (identity-service), which authenticates them (email+password,
  // TOTP) and redirects back to `<this-suite-origin>?code=<code>`. authz-001-7a:
  // that `?code=` is exchanged for an identity-service JWT by the pre-mount
  // bootstrap below (BEFORE AuthProvider mounts) so the JWT seam derives the authed
  // state on the first pass and ProtectedRoute's external redirect cannot abort the
  // in-flight exchange.
  // `redirectBehavior="external"` is kept: unauthenticated users go directly to the
  // front-door, and the local /login route (LoginRedirect) still exists for direct
  // URL access. The cookie session path stays intact (backward compatible) — see
  // BL-029.3 Dev Notes for history.
  const loginFrontdoorUrl = buildLoginFrontdoorUrl();

  // The identity-service bootstrap settles the session BEFORE the auth tree mounts,
  // because the JWT store is in-memory (empty on every fresh load) and AuthProvider
  // resolves auth state once at mount — the JWT must be present first.
  //
  // It runs on EVERY load, handling two cases (see services/identity-code-bootstrap):
  //   - authz-001-7a `?code=` login return → exchange the one-time code for a JWT
  //     first, otherwise ProtectedRoute's external redirect aborts the in-flight
  //     exchange (infinite login bounce).
  //   - plain reload (no `?code=`) → silent refresh from the httpOnly `hwdao_refresh`
  //     cookie so the session rehydrates instead of the admin being logged out on
  //     every refresh.
  //
  // bl-977 / bl-985: the bootstrap now returns a CLASSIFIED verdict, not just
  // done/undone. While it resolves we hold PageLoader. On a definitive verdict
  // (`authed` or `unauthenticated`) we mount the auth tree (an unauthenticated
  // verdict lets ProtectedRoute redirect to the login shell — correct). On a
  // `transient` failure (timeout / 5xx / exhausted connection-retries) we hold a
  // neutral "Reconnecting…" loader with a manual retry instead of painting the
  // sign-in shell — an admin with a valid refresh cookie is never bounced on a blip.
  const [bootstrapState, setBootstrapState] = useState<BootstrapUiState>('resolving');
  // Bumped by the manual retry to re-fire the bootstrap effect after the
  // single-flight is reset (StrictMode-safe: the initial mount does NOT reset, so
  // the double-invoked initial effect still shares the one singleton promise).
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  // E2E bypass (Playwright CI): ProtectedRoute already lets tests through without
  // a real session, and the local-preview env has no CORS-allowed identity-service
  // — the silent refresh throws, which would classify `transient` and hold the
  // Reconnecting loader forever. Under the bypass a transient verdict proceeds to
  // mount instead. Specs that DO need an authed session stub `/token/refresh` and
  // land `authed` before this mapping matters. Never true in a real build.
  const isE2EBypass = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

  // bl-977 review hardening: a synchronous (not React-state) lock so a retry
  // cannot be re-entered while the current bootstrap cycle (initial or a
  // prior retry) is still unsettled — see handleReconnectRetry below for why
  // this must be a ref, not just the `bootstrapState === 'transient'` render
  // gate the Retry button's visibility already relies on.
  const retryLockRef = useRef(false);

  // bl-977 regression #4: latch + state-mirror for the wall-clock escalation.
  //   - escalatedRef: once the ceiling fires we've forced a mounted tree; any
  //     LATER bootstrap resolution (e.g. a retry's `/token/refresh` that lands
  //     after the ceiling) must NOT override it — otherwise a late `transient`
  //     would un-mount the tree back to the Reconnecting hold, past the cap.
  //   - bootstrapStateRef mirrors the latest render's state so the fire-once
  //     escalation timer (created in a mount-only effect, without `bootstrapState`
  //     in its deps) can read the current value without re-arming on every change.
  const escalatedRef = useRef(false);
  const bootstrapStateRef = useRef<BootstrapUiState>(bootstrapState);
  bootstrapStateRef.current = bootstrapState;

  useEffect(() => {
    let cancelled = false;
    retryLockRef.current = true;
    void runIdentityCodeBootstrap().then((status) => {
      // Once the wall-clock ceiling has escalated, ignore a late resolution: the
      // tree is already (deliberately) mounted unauthenticated — a trailing
      // `transient` must not bounce it back to the hold.
      if (cancelled || escalatedRef.current) return;
      retryLockRef.current = false;
      setBootstrapState(status === 'transient' && !isE2EBypass ? 'transient' : 'settled');
    });
    return () => {
      cancelled = true;
    };
  }, [bootstrapAttempt, isE2EBypass]);

  // bl-977 regression #4: the HARD wall-clock ceiling (see
  // TRANSIENT_ESCALATION_CAP_MS above). Mount-only, single-flight: one timer per
  // page load. It fires exactly once and force-settles ONLY if the app is still
  // parked pre-mount (either holding `transient` or resolving an in-flight retry) —
  // if a definitive verdict already mounted the tree (`settled`), it is a no-op.
  // StrictMode's double-invoked effect is safe: the cleanup clears the first
  // timer, leaving a single live timer, and the escalation is idempotent
  // (escalatedRef latch + a same-value setState). It NEVER calls
  // resetIdentityCodeBootstrap / runIdentityCodeBootstrap — no token rotation
  // (bl-789 untouched); it is a pure UI-verdict downgrade.
  useEffect(() => {
    // bl-1247: arm the auth-verdict ceiling from the SAME page-load origin as the
    // escalation cap, so `isInitialSessionResolving` measures its bound from mount.
    sessionResolveOriginMs = Date.now();
    const timer = setTimeout(() => {
      if (escalatedRef.current || bootstrapStateRef.current === 'settled') return;
      escalatedRef.current = true;
      setBootstrapState('settled');
    }, TRANSIENT_ESCALATION_CAP_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleReconnectRetry = useCallback(() => {
    // bl-789 hardening (code-level, not just render-timing): refuse to start a
    // retry while the current bootstrap cycle is still unsettled. Two
    // concurrent `/token/refresh` calls would each try to rotate the SAME
    // httpOnly refresh cookie; the loser presents an already-consumed token →
    // `ReuseDetected` → the WHOLE token family is revoked (hard logout
    // everywhere) — strictly worse than the transient loader this is meant to
    // resolve. The Retry button is only rendered once the CURRENT cycle has
    // already settled to `transient`, so this is normally unreachable through
    // the UI — but that reachability argument rests on React's render timing,
    // not on code. `retryLockRef` is a synchronous flag (unlike `useState`, it
    // can't be "batched away"), so two invocations landing in the same tick —
    // e.g. a fast double-click, or a future second retry entry point — still
    // only ever start ONE cycle.
    if (retryLockRef.current) return;
    retryLockRef.current = true;
    // NB: this deliberately does NOT reset the wall-clock escalation ceiling
    // (TRANSIENT_ESCALATION_CAP_MS is absolute from page load). An admin may
    // Retry within the window, but a retry that itself re-lands `transient` does
    // not push the deadline out — otherwise a stuck upstream could hold the loader
    // forever behind repeated clicks. The ceiling is the terminal guarantee.
    // Clear the settled `transient` single-flight so the next run re-attempts the
    // silent refresh, then re-fire the effect. (Reset is only ever called on an
    // explicit user retry — never on the initial mount — so the StrictMode
    // single-flight guarantee is preserved.)
    resetIdentityCodeBootstrap();
    setBootstrapState('resolving');
    setBootstrapAttempt((n) => n + 1);
  }, []);

  if (bootstrapState === 'resolving') {
    return <PageLoader />;
  }

  if (bootstrapState === 'transient') {
    return <ReconnectingLoader onRetry={handleReconnectRetry} />;
  }

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider config={{ apiBaseUrl: oracleBridgeUrl, authMode: 'identity-jwt', identityServiceUrl: import.meta.env.VITE_IDENTITY_SERVICE_URL, loginFrontDoorUrl: import.meta.env.VITE_LOGIN_FRONTDOOR_URL, e2eBypass: import.meta.env.VITE_E2E_AUTH_BYPASS === 'true', devBypass: import.meta.env.VITE_DEV_AUTH_BYPASS === 'true', getIdentityJwt: () => getIdentityJwt(),
          // bl-1247: hold the unauthenticated verdict while the pre-mount silent
          // /token/refresh is still genuinely in flight (bounded by
          // SESSION_RESOLVE_CEILING_MS). Without this the bl-977 hold in
          // @hello-world-dao/auth collapses to a single setTimeout(…, 0) and an admin
          // holding a valid hwdao_refresh cookie is bounced to the login shell the
          // macrotask after the 12s escalation force-mounts the tree. `isInitialSessionResolving`
          // is a STABLE module-scope reference sourced from the bootstrap's OWN in-flight
          // flag (NOT bootstrapState — see the Trap-1 note at the predicate definition).
          isInitialSessionResolving }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Login redirect - no authentication required (redirects to the login front-door) */}
              <Route path="/login" element={<LoginRedirect loginUrl={loginFrontdoorUrl} />} />

              {/* Unauthorized page - accessible to authenticated non-admin users (AC2) */}
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Spike: Tiptap editor validation (BL-008.3.1) - temporary, no auth required */}
              <Route path="/blog/spike-editor" element={<EditorSpike />} />

              {/* Admin-only routes — require admin role (AC4) */}
              <Route
                element={
                  <ProtectedRoute
                    requiredRole="admin"
                    unauthorizedComponent={<Unauthorized />}
                    loginUrl={loginFrontdoorUrl}
                    redirectBehavior="external"
                    fallback={<AuthFallback />}
                  >
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                {/* Operations group */}
                <Route index element={<AdminDashboard />} />
                <Route path="/users" element={<UserManagement />} />
                <Route path="/users/:user_id" element={<UserDetail />} />
                <Route path="/members" element={<MemberManagement />} />
                <Route path="/kyc" element={<KYCManagement />} />
                <Route path="/tax-forms" element={<TaxFormReview />} />
                <Route path="/property-triage" element={<PropertyTriage />} />
                <Route path="/governance" element={<GovernanceOversight />} />
                <Route path="/orgs/new" element={<CreateOfficialOrg />} />
                <Route path="/orgs/members/new" element={<AddMemberToOrg />} />
                <Route path="/treasury" element={<TreasuryLayout />}>
                  <Route index element={<Holdings />} />
                  <Route path="sub-accounts" element={<SubAccounts />} />
                  <Route path="transfers" element={<InternalTransfers />} />
                  <Route path="patronage" element={<Patronage />} />
                  <Route path="intercompany" element={<EntityTransfers />} />
                  <Route path="digital-assets" element={<DigitalAssets />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="payouts" element={<Payouts />} />
                </Route>
                <Route path="/monitoring" element={<SystemMonitoring />} />
                <Route path="/tokens" element={<TokenManagement />} />
                <Route path="/held-burns" element={<HeldBurns />} />
                <Route path="/lp-locks" element={<LpLocks />} />
                <Route path="/vesting" element={<VestingSchedules />} />
                <Route path="/vesting/:id" element={<VestingDetailPage />} />
                <Route path="/marketplace/fees" element={<MarketplaceFeeSchedule />} />
                <Route path="/marketplace/transactions" element={<MarketplaceTransactions />} />

                {/* Content & Access group (admin-only) */}
                <Route path="/uat-invites" element={<UatInvites />} />
                <Route path="/moderation" element={<ContentModeration />} />
                <Route path="/roles" element={<RoleManagement />} />
                <Route path="/events" element={<EventsPage />} />
              </Route>

              {/* Blog routes — accessible to admin OR author (BL-096, AC1/AC3) */}
              <Route
                element={
                  <ProtectedRoute
                    requiredRoles={['admin', 'author']}
                    unauthorizedComponent={<Unauthorized />}
                    loginUrl={loginFrontdoorUrl}
                    redirectBehavior="external"
                    fallback={<AuthFallback />}
                  >
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/blog" element={<BlogDashboard />} />
                <Route path="/blog/editor/new" element={<BlogEditorPage />} />
                <Route path="/blog/editor/:id" element={<BlogEditorPage />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
